import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { useFileFormatValidator } from 'hooks/useFileFormatValidator'
import {
  calculateTotalMediaDuration,
  calculateTotalFilesSize
} from 'utils/media'
import { createMediaItems, assignStartTimesAndZIndex } from './helpers'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { IMediaItem, IUploadMediaItem } from 'types/media'
import store from 'store'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { checkStorageBeforeUpload } from 'store/features/user/utils'
import { dispatch } from 'store'
import { useVisualMediaPlacement } from 'hooks/timeline/useVisualMediaPlacement'

// TODO: Get rid of "add" and "upload" callbacks after fixing all unappropriate usage of this hook.
// write add and uplaod logic directly in this hook
const useProjectMediaUpload = (
  onAddMediaItems?: (mediaFiles: IMediaItem[]) => void,
  onUploadMedias?: (uploadItems: IUploadMediaItem[]) => void
) => {
  const mediaItemsDuration = useSelector(
    projectDataSelectors.mediaItemsDuration
  )
  const { isDurationAllowed } = useDurationLimitCheck()
  const validateFiles = useFileFormatValidator('visualMedia')
  const { setAlertPopupsData } = useAlertPopupsContext()
  const getPlacement = useVisualMediaPlacement()

  const handleFilesUpload = useCallback(
    async (files: File[] | FileList) => {
      if (!files || files.length === 0) return

      const validFiles = validateFiles(Array.from(files))
      if (validFiles.length === 0) return

      // Check if total file size exceeds available storage
      const totalFilesSize = calculateTotalFilesSize(validFiles)

      const { canUpload } = await checkStorageBeforeUpload(
        totalFilesSize,
        dispatch,
        store.getState
      )

      if (!canUpload) {
        setAlertPopupsData({
          fullStoragePopup: {
            isOpen: true
          }
        })
        return
      }

      // Get the most current canvas dimensions and media items from Redux state
      const state = store.getState()
      const currentDimensions = projectDataSelectors.defaultDimensions(state)
      const { width: currentWidth, height: currentHeight } = currentDimensions

      // Create media items without position assignment
      const mediaItems = await createMediaItems(validFiles, {
        width: currentWidth,
        height: currentHeight
      })

      const totalNewDuration = calculateTotalMediaDuration(mediaItems)

      if (!isDurationAllowed(totalNewDuration)) {
        return
      }

      const placement = getPlacement(totalNewDuration)

      const positionedMediaItems = assignStartTimesAndZIndex(
        mediaItems,
        placement.startTime,
        placement.zIndex
      )

      onAddMediaItems?.(positionedMediaItems)

      const uploadItems = Array.from(validFiles).map((file, index) => {
        const mediaFile = positionedMediaItems[index]
        return {
          id: mediaFile.id,
          type: mediaFile.type,
          file
        }
      })

      onUploadMedias?.(uploadItems)
    },
    // TODO: Recheck this, you sure you don't need to pass isDurationAllowed, onAddMediaItems, onUploadMedias?
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediaItemsDuration, setAlertPopupsData]
  )

  return handleFilesUpload
}

export default useProjectMediaUpload
